import i18n from 'i18next';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import routes from 'routes';
import { setLocale, setMenuSize, setRefreshDone, turnMessageOn } from 'store/app/actions';
import { LicenseType, MenuSize } from 'store/app/enums';
import { APP_LOCALE, APP_MENU_SIZE } from 'store/app/reducer';
import { getRefreshDone } from 'store/app/selectors';
import {
  fetchMeRequest,
  setThirdPartySSO,
  updateCurrentUserPreferencesRequest,
  updateCurrentUserPreferencesSuccess,
} from 'store/auth/actions';
import { getAuthLoading, getAuthUser, getAuthUserLicence } from 'store/auth/selectors';
import { fetchOrganisationsRequest } from 'store/organisation/actions';
import { getOrgOrganisations } from 'store/organisation/selectors';
import {
  fetchAdvancedUserRightsRequest,
  fetchUsersRequest,
} from 'store/user/actions';
import {
  getUserLoading,
  getUserMembers,
  selectUserAdvancedUserRights,
} from 'store/user/selectors';
import {
  PreferenceSections,
  PreferenceActions,
  usePreferences,
  Preferences,
  setPreferencesInSection,
} from 'utils/preferences';
import {
  A11Y_FOCUS_STRONG,
  A11Y_HIGH_CONTRAST,
  A11Y_REDUCE_MOTION,
  A11Y_TEXT_SCALE,
  A11Y_TEXT_SPACING,
  A11Y_UNDERLINE_LINKS,
  applyA11yPreferences,
  A11Y_LOGIN_MODE_CLASS,
  a11yPreferencesToPreferenceRecords,
  consumeLoginA11yModePreset,
  normalizeA11yPreferences,
  syncLoginA11yModeFromStorage,
} from 'utils/apply-a11y-preferences';
import { startInertTableMeasureRows } from 'utils/inert-table-measure-rows';
import { DEFAULT_LOCALE, LOCALES, resolveLocale, type LocaleCode } from 'i18n/locales';
import { usePrevious } from 'hooks/usePrevious';
import { getCommonCountries } from 'store/common/selectors';
import { fetchCountriesRequest } from 'store/common/actions';
import { MessageStates } from 'containers/MessageBox';
import { MessageData } from 'store/app/types';
import { useEffect } from 'react';
import { isMultipleLanguagesDisabled } from 'utils/feature-flag-utils';
import { useReviewModal } from 'utils/use-review-modal';
import { getOrgMappedReturns } from 'store/organisation/selectors';
import { GEN_TAX_CALLED_PAGE_KEY, isNonRestorableCalledPage } from 'utils/check-page-loaded';

export type LayoutProps = {
  menuSize: MenuSize;
  children?: React.ReactNode;
};

export function Layout({ menuSize, children }: LayoutProps) {
  const loggedUser = useSelector(getAuthUser);
  const authLoading = useSelector(getAuthLoading);
  const refreshDone = useSelector(getRefreshDone);
  const organisations = useSelector(getOrgOrganisations);
  const members = useSelector(getUserMembers);
  const usersLoading = useSelector(getUserLoading);
  const userLicence = useSelector(getAuthUserLicence);
  const countries = useSelector(getCommonCountries);
  const advancedUserRights = useSelector(selectUserAdvancedUserRights);
  const mappedReturns = useSelector(getOrgMappedReturns);
  const { checkAndShowReviewModal } = useReviewModal();

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // Track only the userUuid to avoid re-triggering effect when profile picture changes
  const userUuid = loggedUser?.userUuid;
  const preferences = usePreferences();
  const querpar = new URLSearchParams(location.search);
  const ssoLoginName = querpar.get('ssoLoginName');
  const ssoExtraInfo = querpar.get('ts');
  const additionnalInfo = ssoExtraInfo ? `&ts=${ssoExtraInfo}` : '';
  const ssoCallbackUrl = querpar.get('serviceurl') + additionnalInfo;

  useEffect(() => {
    if (ssoLoginName && ssoCallbackUrl) {
      dispatch(setThirdPartySSO({ ssoLoginName, ssoCallbackUrl }));
    }
  }, [ssoLoginName]);

  // Dev: ?resetFeatureDots=1 clears dismissed feature dots for testing.
  const featureDotsResetEnabled = process.env.NODE_ENV === 'development';
  const hasResetFeatureDots = React.useRef(false);
  if (querpar.get('resetFeatureDots') !== '1') {
    hasResetFeatureDots.current = false;
  }
  useEffect(() => {
    if (
      !featureDotsResetEnabled ||
      querpar.get('resetFeatureDots') !== '1' ||
      !loggedUser ||
      hasResetFeatureDots.current
    ) {
      return;
    }
    hasResetFeatureDots.current = true;
    const raw = loggedUser?.prefDetails?.[1];
    let prefs: Preferences[] = [];
    if (raw) {
      try {
        prefs = typeof raw === 'string' ? JSON.parse(raw) : raw;
      } catch {
        prefs = [];
      }
    }
    const filtered = prefs.filter(
      (p: { section: string }) => p.section !== PreferenceSections.FEATURE_DOTS
    );
    // Immediately update Redux so UI reflects reset (for testing when API may be slow/fail)
    dispatch(updateCurrentUserPreferencesSuccess(filtered));
    dispatch(updateCurrentUserPreferencesRequest(filtered));
    // Remove query param after brief delay (skip fetchMe to avoid overwriting optimistic update)
    const t = setTimeout(() => {
      navigate(location.pathname);
    }, 300);
    return () => clearTimeout(t);
  }, [querpar.get('resetFeatureDots'), loggedUser?.userUuid]);
  const loggedIn =
    !location.pathname.includes(routes.signUp.root) &&
    !location.pathname.includes(routes.resetPassword.root) &&
    !location.pathname.includes(routes.termsOfUse) &&
    !location.pathname.includes(routes.privacyPolicy) &&
    !location.pathname.includes(routes.accessibilityStatement) &&
    // !location.pathname.includes(routes.login) &&
    !location.pathname.includes(routes.notFound); //&&

  //!location.pathname.includes(routes.home);
  const prevLoggedUser = usePrevious(loggedUser);

  React.useEffect(() => {
    if (!location.pathname.includes('redirector')) {
      if (refreshDone) {
        navigate(
          routes.tempRedirector.replace(
            ':route',
            location.pathname.replace(/\//g, '<slash>') + location.search
          )
        );
        dispatch(setRefreshDone());
      }
      // ! FOR LOADING DETAILS ON FIRST CALL
      if (loggedIn) {
        if (
          !location.pathname.includes(routes.login) &&
          !location.pathname.includes('redirector') &&
          !isNonRestorableCalledPage(location.pathname)
        )
          localStorage.setItem(GEN_TAX_CALLED_PAGE_KEY, location.pathname);
        dispatch(fetchMeRequest());
      }
    }
  }, []);

  // Ensure auth bootstrap also runs when transitioning from public routes
  // (e.g. SSO signup callback) into protected pages without a full refresh.
  React.useEffect(() => {
    const isRedirectorRoute = location.pathname.includes('redirector');
    const isLoginRoute = location.pathname.includes(routes.login);
    const shouldBootstrapAuth =
      loggedIn && !isRedirectorRoute && !isLoginRoute && !loggedUser && !authLoading;

    if (shouldBootstrapAuth) {
      dispatch(fetchMeRequest());
    }
  }, [location.pathname, loggedIn, loggedUser, authLoading, dispatch]);

  React.useEffect(() => {
    if (
      loggedUser &&
      JSON.stringify(loggedUser) !== JSON.stringify(prevLoggedUser)
    ) {
      const preferenceSection = PreferenceSections.APP;
      const preferenceAction = PreferenceActions.GET;
      const preference = { name: APP_MENU_SIZE };
      const userUuid = loggedUser?.userUuid;

      const savedMenuSize = preferences(
        preferenceAction,
        preferenceSection,
        preference,
        userUuid
      );

      savedMenuSize && dispatch(setMenuSize(savedMenuSize, userUuid));

      const savedLocale = isMultipleLanguagesDisabled()
        ? undefined
        : (preferences(
            preferenceAction,
            preferenceSection,
            { name: APP_LOCALE },
            userUuid
          ) as LocaleCode | undefined);
      // Restore saved language when valid; otherwise keep English default
      dispatch(
        setLocale(
          isMultipleLanguagesDisabled()
            ? DEFAULT_LOCALE
            : resolveLocale(
                savedLocale && LOCALES.some(({ code }) => code === savedLocale)
                  ? savedLocale
                  : undefined
              ),
          userUuid
        )
      );
      if (!countries || countries.length === 0) {
        dispatch(fetchCountriesRequest());
      }
      if (
        (userLicence?.licenseType === LicenseType.TRIAL &&
          userLicence?.licenseDaysLeft > 0) ||
        userLicence?.licenseType === LicenseType.PAID
      ) {
        if (!organisations) {
          dispatch(fetchOrganisationsRequest());
        }
        if (!members) {
          dispatch(fetchUsersRequest());
        }
        if (!advancedUserRights) {
          dispatch(fetchAdvancedUserRightsRequest());
        }
      }
      let message: MessageData | null = null;
      if (
        userLicence?.licenseType === LicenseType.BLOCKED &&
        userLicence?.licenceExpireDate &&
        new Date(userLicence.licenceExpireDate) < new Date()
      ) {
        message = {
          title: i18n.t('billing.license-expired-title'),
          description: i18n.t('billing.license-expired-description'),
          type: MessageStates.WARNING,
        };
      }
      if (
        userLicence?.licenseType === LicenseType.TRIAL &&
        userLicence?.licenseDaysLeft === 0
      ) {
        message = {
          title: i18n.t('billing.trial-expired-title'),
          description: i18n.t('billing.license-expired-description'),
          type: MessageStates.WARNING,
        };
      }
      if (
        userLicence?.licenseType === LicenseType.PAID &&
        userLicence?.licenseDaysLeft <= 0
      ) {
        message = {
          title: i18n.t('billing.auto-renewal-failed-title'),
          description: i18n.t('billing.auto-renewal-failed-description'),
          type: MessageStates.WARNING,
        };
      }
      if (message) {
        dispatch(turnMessageOn(message));
      }
    }
  }, [userUuid, userLicence]);

  // Apply personal accessibility preferences globally when the user loads or prefs change
  React.useEffect(() => {
    if (!loggedUser?.userUuid) {
      syncLoginA11yModeFromStorage();
      return;
    }
    if (typeof document !== 'undefined') {
      document.documentElement.classList.remove(A11Y_LOGIN_MODE_CLASS);
    }

    const loginA11yPreset = consumeLoginA11yModePreset();
    if (loginA11yPreset) {
      setPreferencesInSection(
        PreferenceSections.APP,
        a11yPreferencesToPreferenceRecords(loginA11yPreset),
        loggedUser,
        dispatch
      );
      applyA11yPreferences(loginA11yPreset);
      return;
    }

    const uuid = loggedUser.userUuid;
    const a11yPrefs = normalizeA11yPreferences({
      [A11Y_TEXT_SCALE]: preferences(
        PreferenceActions.GET,
        PreferenceSections.APP,
        { name: A11Y_TEXT_SCALE },
        uuid
      ),
      [A11Y_TEXT_SPACING]: preferences(
        PreferenceActions.GET,
        PreferenceSections.APP,
        { name: A11Y_TEXT_SPACING },
        uuid
      ),
      [A11Y_HIGH_CONTRAST]: preferences(
        PreferenceActions.GET,
        PreferenceSections.APP,
        { name: A11Y_HIGH_CONTRAST },
        uuid
      ),
      [A11Y_UNDERLINE_LINKS]: preferences(
        PreferenceActions.GET,
        PreferenceSections.APP,
        { name: A11Y_UNDERLINE_LINKS },
        uuid
      ),
      [A11Y_REDUCE_MOTION]: preferences(
        PreferenceActions.GET,
        PreferenceSections.APP,
        { name: A11Y_REDUCE_MOTION },
        uuid
      ),
      [A11Y_FOCUS_STRONG]: preferences(
        PreferenceActions.GET,
        PreferenceSections.APP,
        { name: A11Y_FOCUS_STRONG },
        uuid
      ),
    });
    applyA11yPreferences(a11yPrefs);
  }, [loggedUser?.userUuid, loggedUser?.prefDetails?.[1]]);

  React.useEffect(() => startInertTableMeasureRows(), []);

  // Check for review modal when user and mapped returns are loaded
  React.useEffect(() => {
    if (
      loggedUser &&
      mappedReturns !== undefined &&
      organisations !== undefined &&
      !location.pathname.includes(routes.login) &&
      !location.pathname.includes('redirector')
    ) {
      // Delay check slightly to ensure all data is loaded
      const timer = setTimeout(() => {
        checkAndShowReviewModal();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [loggedUser, mappedReturns, organisations, location.pathname]);

  const isLoginRoute = location.pathname.includes(routes.login);
  const isSignUpRoute = location.pathname.includes(routes.signUp.root);
  const isResetPasswordRoute = location.pathname.includes(routes.resetPassword.root);
  // Only apply organisations layout styling on the entity detail page, not the list
  const isOrganisationsRoute = location.pathname.startsWith(
    routes.organisations.organisation.replace(':id', '')
  );

  const baseLayoutClass =
    !loggedIn || isLoginRoute || isSignUpRoute || isOrganisationsRoute
      ? 'layout'
      : menuSize === MenuSize.WIDE
      ? 'layout-narrow'
      : 'layout-wide';

  let layoutClass = baseLayoutClass;
  if (isLoginRoute || isResetPasswordRoute) {
    layoutClass = `${baseLayoutClass} layout-login`;
  } else if (isSignUpRoute) {
    layoutClass = `${baseLayoutClass} layout-signup`;
  } else if (isOrganisationsRoute) {
    layoutClass = `${baseLayoutClass} layout-organisations`;
  }

  return <div className={layoutClass}>{children}</div>;
}
